import {
  buildParentMap,
  type Crop,
  type ImageElement,
  maskClipPath,
  maskPath,
  rotatePoint,
  worldBox,
} from "@cva/shared";
import { Check, RotateCcw } from "lucide-react";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef } from "react";
import { useEnv } from "./env";
import { useEditor, useEditorApi } from "./store";

/** Crop implied by `fit` (cover/contain) when the element has no explicit crop. */
export function effectiveCrop(
  element: ImageElement,
  natural: { width: number; height: number },
): Crop {
  if (element.crop) return element.crop;
  const frameRatio = element.width / element.height;
  const imageRatio = natural.width / natural.height;
  if (element.fit === "contain" || Math.abs(frameRatio - imageRatio) < 1e-6)
    return { x: 0, y: 0, width: 1, height: 1 };
  if (imageRatio > frameRatio) {
    const width = frameRatio / imageRatio;
    return { x: (1 - width) / 2, y: 0, width, height: 1 };
  }
  const height = imageRatio / frameRatio;
  return { x: 0, y: (1 - height) / 2, width: 1, height };
}

type Drag =
  | { type: "move"; start: { x: number; y: number }; crop: Crop }
  | {
      type: "scale";
      corner: "nw" | "ne" | "se" | "sw";
      crop: Crop;
      start: { x: number; y: number };
    };

/**
 * Crop mode (double click on an image): the whole image is shown behind the
 * frame; drag it to move, drag its corners to zoom.
 */
export function CropOverlay({ element, zoom }: { element: ImageElement; zoom: number }) {
  const store = useEditorApi();
  const env = useEnv();
  const doc = useEditor((s) => s.doc);
  const asset = env.assets.get(element.assetId);
  const drag = useRef<Drag | null>(null);
  const box = worldBox(doc, element.id, buildParentMap(doc));
  const natural = { width: asset?.width ?? element.width, height: asset?.height ?? element.height };
  const crop = effectiveCrop(element, natural);

  useEffect(() => {
    // Long-lived: changes of others show meanwhile.
    store.getState().beginGesture({ live: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Enter") store.getState().setCropping(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      store.getState().endGesture();
    };
  }, [store]);

  if (!box || !asset) return null;
  // Full image in the element's local frame
  const imageWidth = box.width / crop.width;
  const imageHeight = box.height / crop.height;
  const left = -crop.x * imageWidth;
  const top = -crop.y * imageHeight;

  const toLocalDelta = (event: ReactPointerEvent, start: { x: number; y: number }) => {
    const dx = (event.clientX - start.x) / zoom;
    const dy = (event.clientY - start.y) / zoom;
    const rotated = rotatePoint({ x: dx, y: dy }, { x: 0, y: 0 }, -box.rotation);
    return rotated;
  };

  const apply = (next: Crop) => {
    store.getState().update((draft) => {
      const target = draft.elements[element.id];
      if (target?.type === "image") target.crop = next;
    });
  };

  const onPointerDown = (
    event: ReactPointerEvent,
    type: Drag["type"],
    corner?: "nw" | "ne" | "se" | "sw",
  ) => {
    event.stopPropagation();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    const start = { x: event.clientX, y: event.clientY };
    drag.current =
      type === "move" ? { type, start, crop } : { type: "scale", corner: corner!, crop, start };
  };

  const onPointerMove = (event: ReactPointerEvent) => {
    const current = drag.current;
    if (!current) return;
    const delta = toLocalDelta(event, current.start);
    const base = current.crop;
    const baseWidth = box.width / base.width;
    const baseHeight = box.height / base.height;
    if (current.type === "move") {
      const x = Math.max(0, Math.min(1 - base.width, base.x - delta.x / baseWidth));
      const y = Math.max(0, Math.min(1 - base.height, base.y - delta.y / baseHeight));
      apply({ ...base, x, y });
      return;
    }
    // Zoom around the opposite corner of the image
    const signX = current.corner === "ne" || current.corner === "se" ? 1 : -1;
    const signY = current.corner === "sw" || current.corner === "se" ? 1 : -1;
    const factor = Math.max(
      box.width / baseWidth,
      box.height / baseHeight,
      Math.max(
        (baseWidth + delta.x * signX) / baseWidth,
        (baseHeight + delta.y * signY) / baseHeight,
      ),
    );
    const width = baseWidth * factor;
    const height = baseHeight * factor;
    const baseLeft = -base.x * baseWidth;
    const baseTop = -base.y * baseHeight;
    const anchorX = signX > 0 ? baseLeft : baseLeft + baseWidth;
    const anchorY = signY > 0 ? baseTop : baseTop + baseHeight;
    let nextLeft = signX > 0 ? anchorX : anchorX - width;
    let nextTop = signY > 0 ? anchorY : anchorY - height;
    // The image must keep covering the frame.
    nextLeft = Math.min(0, Math.max(box.width - width, nextLeft));
    nextTop = Math.min(0, Math.max(box.height - height, nextTop));
    apply({
      x: -nextLeft / width,
      y: -nextTop / height,
      width: box.width / width,
      height: box.height / height,
    });
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  const handle = 12 / zoom;
  return (
    <div
      className="absolute"
      style={{
        left: box.x,
        top: box.y,
        width: box.width,
        height: box.height,
        transform: box.rotation ? `rotate(${box.rotation}deg)` : undefined,
        zIndex: 5,
      }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {/* whole image, dimmed outside the frame */}
      <div
        className="absolute cursor-move"
        style={{ left, top, width: imageWidth, height: imageHeight }}
        onPointerDown={(event) => onPointerDown(event, "move")}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <img
          src={asset.url}
          alt=""
          draggable={false}
          className="pointer-events-none absolute inset-0 h-full w-full opacity-40"
          style={{
            transform:
              element.flipX || element.flipY
                ? `scale(${element.flipX ? -1 : 1}, ${element.flipY ? -1 : 1})`
                : undefined,
          }}
        />
        {(["nw", "ne", "se", "sw"] as const).map((corner) => (
          <div
            key={corner}
            className="absolute rounded-full border-brand-600 bg-white shadow"
            style={{
              width: handle,
              height: handle,
              borderWidth: 1.5 / zoom,
              left: corner.includes("w") ? -handle / 2 : undefined,
              right: corner.includes("e") ? -handle / 2 : undefined,
              top: corner.includes("n") ? -handle / 2 : undefined,
              bottom: corner.includes("s") ? -handle / 2 : undefined,
              cursor: corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize",
            }}
            onPointerDown={(event) => onPointerDown(event, "scale", corner)}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          />
        ))}
      </div>
      {/* the frame shows the cropped image at full opacity */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={
          element.mask
            ? { clipPath: maskClipPath(element.mask, box.width, box.height) }
            : { borderRadius: element.radius, outline: `${2 / zoom}px solid rgb(79 70 229)` }
        }
      >
        <img
          src={asset.url}
          alt=""
          draggable={false}
          className="absolute max-w-none"
          style={{
            left,
            top,
            width: imageWidth,
            height: imageHeight,
            transform:
              element.flipX || element.flipY
                ? `scale(${element.flipX ? -1 : 1}, ${element.flipY ? -1 : 1})`
                : undefined,
          }}
        />
      </div>
      {element.mask ? (
        // The form (mask) the image moves in.
        <svg
          className="pointer-events-none absolute inset-0 overflow-visible"
          width={box.width}
          height={box.height}
          aria-hidden="true"
        >
          <path
            d={maskPath(element.mask, box.width, box.height)}
            fill="none"
            stroke="rgb(79 70 229)"
            strokeWidth={2 / zoom}
          />
        </svg>
      ) : null}
      <div
        className="absolute left-1/2 flex gap-1 rounded-lg bg-zinc-900 p-1 text-white shadow-lg"
        style={{
          top: `calc(100% + ${12 / zoom}px)`,
          transform: `translateX(-50%) scale(${1 / zoom})`,
          transformOrigin: "top center",
        }}
      >
        <button
          type="button"
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-white/10"
          onClick={() => apply(effectiveCrop({ ...element, crop: null, fit: "cover" }, natural))}
        >
          <RotateCcw className="size-3.5" /> Zurücksetzen
        </button>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md bg-brand-600 px-2 py-1 text-xs hover:bg-brand-700"
          onClick={() => store.getState().setCropping(null)}
        >
          <Check className="size-3.5" /> Fertig
        </button>
      </div>
    </div>
  );
}
